import { apiSlice } from './apiSlice';
import { Role, Wallet, LoginAsTokenResponse, VerifyLoginTokenResponse } from '@/types';
import { setUser, UserResponse } from '../slices/userSlice';
import { setAvailableRoles } from '../slices/availableRolesSlice';
interface ChangePasswordResponse {
  status: number;
  message: string;
}
/** A login can either finish immediately or begin the MFA challenge. */
export type LoginResponse =
  | {
      success?: true;
      accessToken: string;
      refreshToken?: string;
      type: string;
      role: AuthRole;
      transactionCode?: string | null;
    }
  | {
      success: true;
      mfaRequired: true;
      mfaMethod?: MfaMethod | null;
      userId: string | number;
      userType: string;
      accessToken: null;
    }
  | {
      success: true;
      mfaSetupRequired: true;
      mfaMethod?: MfaMethod | null;
      userId: string | number;
      userType: string;
      accessToken: null;
    };

export type AuthRole = { name: string; id: number };
export type AuthTokens = { accessToken: string; refreshToken?: string; type: string };
export type MfaMethod = 'GOOGLE' | 'TELEGRAM';

export type TelegramMfaLinkResponse = {
  linkUrl: string;
  code: string;
  expiresInSeconds: number;
};
export type TelegramMfaMutationResponse = {
  status: 'ACTIVE' | 'INACTIVE';
  method: 'telegram';
};
type MfaMethodStatus = {
  configured: boolean;
  pending: boolean;
};
export type MfaStatusResponse = {
  isMfaEnabled: boolean;
  activeMethod: MfaMethod | null;
  totp?: MfaMethodStatus | boolean;
  telegram?: MfaMethodStatus | boolean;
};
export const authApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    login: builder.mutation<LoginResponse, { username: string; password: string }>({
      query: (body) => ({
        url: '/auth/login',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['User'],
    }),
    setupMfa: builder.mutation<
      { qrCode: string; secret: string } | { status: 'ACTIVE' | 'INACTIVE'; message?: string },
      | { usernameOrEmail: string; password: string; status: 'ACTIVE' }
      | { usernameOrEmail?: string; password?: string; status: 'INACTIVE'; token: string }
    >({
      query: (body) => ({ url: '/mfa/setup', method: 'POST', body }),
      invalidatesTags: ['MfaStatus', 'User'],
    }),
    confirmMfaSetup: builder.mutation<{ status: 'ACTIVE'; message?: string }, { token: string }>({
      query: (body) => ({ url: '/mfa/setup/confirm', method: 'POST', body }),
      invalidatesTags: ['MfaStatus', 'User'],
    }),
    verifyMfa: builder.mutation<
      AuthTokens,
      { username?: string; userId?: string | number; userType?: string; token: string }
    >({
      query: (body) => ({ url: '/auth/mfa/verify', method: 'POST', body }),
      invalidatesTags: ['User'],
    }),
    // Telegram MFA hidden for now
    // sendLoginTelegramOtp: builder.mutation<
    //   { sent: true; expiresInSeconds: number },
    //   { userId: string | number; userType: string }
    // >({
    //   query: (body) => ({ url: '/auth/mfa/send-otp', method: 'POST', body }),
    // }),
    // startTelegramLink: builder.mutation<TelegramMfaLinkResponse, void>({
    //   query: () => ({ url: '/mfa/telegram/link', method: 'POST' }),
    //   invalidatesTags: ['MfaStatus'],
    // }),
    // confirmTelegramLink: builder.mutation<TelegramMfaMutationResponse, { token: string }>({
    //   query: (body) => ({ url: '/mfa/telegram/link/confirm', method: 'POST', body }),
    //   invalidatesTags: ['MfaStatus', 'User'],
    // }),
    // sendTelegramDisableOtp: builder.mutation<{ sent: true; expiresInSeconds: number }, void>({
    //   query: () => ({ url: '/mfa/telegram/send-otp', method: 'POST' }),
    // }),
    // unlinkTelegramMfa: builder.mutation<TelegramMfaMutationResponse, { token: string }>({
    //   query: (body) => ({ url: '/mfa/telegram/unlink', method: 'POST', body }),
    //   invalidatesTags: ['MfaStatus', 'User'],
    // }),
    getMfaStatus: builder.query<MfaStatusResponse, void>({
      query: () => ({ url: '/mfa/status', method: 'GET', params: { ts: Date.now() } }),
      providesTags: ['MfaStatus'],
    }),
    // new captcha endpoint
    getCaptcha: builder.query<any, void>({
      query: () => ({
        url: '/captcha',
        method: 'POST',
      }),
    }),

    logout: builder.mutation<any, void>({
      query: () => ({
        url: '/auth/logout',
        method: 'POST',
      }),
    }),
    getUser: builder.query<UserResponse, void>({
      query: () => ({ url: '/users/me', method: 'GET', params: { ts: Date.now() } }),

      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(setUser(data));
        } catch (err) {
          console.error(err);
        }
      },
      providesTags: ['User'],
    }),
    availbleRoles: builder.query<Role[], void>({
      query: () => ({
        url: '/role/available',
        method: 'GET',
        params: { ts: Date.now() },
      }),
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(setAvailableRoles(data));
        } catch (err) {
          console.error(err);
        }
      },
      // providesTags: ['User'],
    }),
    changePassword: builder.mutation<
      { success: boolean; status?: string },
      { oldPassword: string; newPassword: string }
    >({
      query: (body) => ({
        url: '/users/me/change-password',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['User'],
    }),
    OwnerChangePassword: builder.mutation<
      ChangePasswordResponse,
      { oldPassword: string; newPassword: string }
    >({
      query: (body) => ({
        url: '/admin/change-password',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['User'],
    }),

    getWallet: builder.query<Wallet[], void>({
      query: () => ({
        url: '/users/me/wallets',
        method: 'GET',
        params: { ts: Date.now() },
      }),
      providesTags: ['Wallet'],
    }),

    // "Login as" - generates a one-time code for logging in as a downline user on the agent panel
    getLoginAsToken: builder.query<LoginAsTokenResponse, { userId: string | number }>({
      query: ({ userId }) => ({
        url: `/auth/gettoken/${userId}`,
        method: 'GET',
        responseHandler: 'text',
      }),
      // backend may respond with a raw code, a JSON-quoted string, or an object - normalize all of them
      transformResponse: (response: string): LoginAsTokenResponse => {
        let code = (response ?? '').trim();
        try {
          const parsed = JSON.parse(code);
          if (typeof parsed === 'string') {
            code = parsed;
          } else if (parsed && typeof parsed === 'object') {
            code = parsed.code ?? parsed.token ?? code;
          }
        } catch {
          // not JSON - use the raw text as-is
        }
        return { code };
      },
    }),
    verifyLoginToken: builder.query<VerifyLoginTokenResponse, { token: string }>({
      query: ({ token }) => ({
        url: `/auth/verify-token/${token}`,
        method: 'GET',
      }),
    }),

    // Whether the logged-in user is the owner's master account
    getIsMasterOwner: builder.query<boolean, void>({
      query: () => ({
        url: '/users/is/master-owner/user',
        method: 'GET',
        params: { ts: Date.now() },
      }),
    }),
  }),
});

export const {
  useLoginMutation,
  useSetupMfaMutation,
  useConfirmMfaSetupMutation,
  useVerifyMfaMutation,
  // Telegram MFA hidden for now
  // useSendLoginTelegramOtpMutation,
  // useStartTelegramLinkMutation,
  // useConfirmTelegramLinkMutation,
  // useSendTelegramDisableOtpMutation,
  // useUnlinkTelegramMfaMutation,
  useGetMfaStatusQuery,
  useGetCaptchaQuery,
  useLazyGetCaptchaQuery,
  useLogoutMutation,
  useGetUserQuery,
  useChangePasswordMutation,
  useAvailbleRolesQuery,
  useGetWalletQuery,
  useOwnerChangePasswordMutation,
  useLazyGetLoginAsTokenQuery,
  useLazyVerifyLoginTokenQuery,
  useGetLoginAsTokenQuery,
  useGetIsMasterOwnerQuery,
} = authApi;
